import { useState } from "react";

import { api } from "@/lib/api";
import { formatDateTime, useAsync } from "@/lib/hooks";

interface Advice {
  id: string;
  reading_id: string;
  model: string;
  response_text: string;
  created_at: string;
}

/**
 * Claude's read on one reading.
 *
 * On demand, never automatic on save — it costs a call, adds latency, and most
 * readings don't need interpreting. Once generated it is saved against the
 * reading, so looking back at what was said is free.
 */
export default function AdvicePanel({ readingId }: { readingId: string }) {
  const existing = useAsync<Advice | null>(
    () => api.get<Advice | null>(`/api/advice/${readingId}`),
    [readingId],
  );
  const [generated, setGenerated] = useState<Advice | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const advice = generated ?? existing.data;

  async function generate() {
    setBusy(true);
    setError(null);
    try {
      setGenerated(await api.post<Advice>(`/api/advice/${readingId}`));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not get advice right now.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card mt-4 p-4">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="unit">What to make of it</h2>
        {advice && (
          <span className="text-xs text-(--color-ink-faint)">
            {formatDateTime(advice.created_at)}
          </span>
        )}
      </div>

      {advice ? (
        <p className="mt-2 whitespace-pre-wrap text-sm">{advice.response_text}</p>
      ) : (
        <p className="mt-1 text-sm text-(--color-ink-muted)">
          Claude can order and explain the steps for this reading. The dose amounts themselves
          are always worked out locally.
        </p>
      )}

      {error && (
        <p role="alert" className="mt-2 text-sm text-(--color-status-high)">
          {error}
        </p>
      )}

      <button
        type="button"
        onClick={generate}
        disabled={busy}
        className="btn btn-quiet mt-3 w-full text-sm"
      >
        {busy ? "Asking Claude…" : advice ? "Ask again" : "Ask Claude"}
      </button>
    </section>
  );
}
